import React, { useState } from 'react';
import { Alert, Col, Empty, Row, Spin, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { PvpLiveCheckKey } from '@/types/pvp';
import { CheckDrawer, HealthList } from './HealthList';
import { LiveCharts } from './LiveCharts';
import { LiveTiles } from './LiveTiles';
import { ProcessTable } from './ProcessTable';
import { isEmpty, isStale, periodLine, staleMessage, type LiveState } from './model';

const { Title, Text } = Typography;

export const EMPTY_TEXT = 'No PvP server has reported yet.';

interface Props {
  state: LiveState;
  /** The check whose drawer starts open (tests); the page starts with none. */
  initialCheck?: PvpLiveCheckKey | null;
}

/**
 * Everything under the controls (layout B): the tiles, then the charts on the
 * left with the health list as a narrow column on the right, then the
 * processes. A failed refresh keeps the last good answer on screen.
 */
export const LiveReport: React.FC<Props> = ({ state, initialCheck = null }) => {
  const [openKey, setOpenKey] = useState<PvpLiveCheckKey | null>(initialCheck);
  const { data, loading, error } = state;
  const openCheck = data?.checks.find((c) => c.key === openKey) ?? null;

  return (
    <>
      {error && (
        <Alert
          type="error"
          showIcon
          message={data ? `Could not refresh: ${error}. Showing the last answer.` : error}
          style={{ marginBottom: 12 }}
        />
      )}
      {!data && loading && <Spin style={{ display: 'block', margin: '24px auto' }} />}
      {data && (
        <Spin spinning={loading}>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
            {periodLine(data)}
          </Text>
          {data.paused && (
            <Alert
              type="warning"
              showIcon
              message="New matches are paused"
              description={
                <>
                  Nobody can join the queue or start a bot match; matches already being played carry
                  on. Resume in <Link to="/config?tab=pvp">Configuration → Realtime PvP</Link>.
                </>
              }
              style={{ marginBottom: 12 }}
            />
          )}
          {isEmpty(data) ? (
            <Empty description={EMPTY_TEXT} style={{ margin: '48px 0' }} />
          ) : (
            <>
              {isStale(data) && (
                <Alert
                  type="warning"
                  showIcon
                  message={staleMessage(data)}
                  style={{ marginBottom: 12 }}
                />
              )}
              <LiveTiles dto={data} />
              <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col xs={24} xl={17}>
                  <LiveCharts dto={data} />
                </Col>
                <Col xs={24} xl={7}>
                  <HealthList checks={data.checks} onOpen={setOpenKey} />
                </Col>
              </Row>
              <section style={{ marginTop: 24 }}>
                <Title level={5} style={{ marginBottom: 0 }}>
                  Processes
                </Title>
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
                  Every PvP process seen in the last 15 minutes. Lost = its heartbeat stopped
                  without a clean stop.
                </Text>
                <ProcessTable processes={data.processes ?? []} />
              </section>
            </>
          )}
        </Spin>
      )}
      <CheckDrawer
        check={openCheck}
        range={data?.range ?? '24h'}
        bucketMin={data?.bucketMin ?? 10}
        onClose={() => setOpenKey(null)}
      />
    </>
  );
};
